import type { World } from '../../shared/types.js';
import { el } from '../ui/dom.js';
import { createSceneKit, type SceneKit } from './scene.js';
import { buildWorldMeshes, type WorldMeshes } from './worldMesh.js';
import { buildRouteMeshes, type RouteMeshes } from './routeMesh.js';
import { OrbitCamera } from '../game/orbitCamera.js';
import { Skyline } from '../game/outroCamera.js';

/**
 * A real city behind the menu.
 *
 * The game's premise is that the track is a place — so the front door shows one, generated
 * by the same code that will generate the one you race, and orbited by the camera that used
 * to fly the pre-race establishing shot. Nothing here is a render of a city; it *is* the
 * city, three hundred milliseconds of the server's time and under a megabyte on the wire.
 *
 * **It is deliberately a luxury.** The paper on top of it is opaque and carries its own
 * contrast, and the gradient underneath is a finished-looking dusk on its own, so every
 * failure mode — a cold server, a browser with no WebGL, a machine that will not hold two
 * cities at once — degrades to a menu that simply never lights up. Nothing waits on it and
 * nothing reads its state.
 *
 * It is also the *second* renderer in the process, which is the one thing about it that
 * could genuinely cost a player something. So it hands its meshes back the moment a race
 * starts (`sleep`) and rebuilds them from the cached payload on the way out (`wake`) rather
 * than sitting on a few hundred thousand triangles of GPU memory the race would rather
 * have. The JSON is kept, because that is a megabyte of system memory to save a round trip.
 */

/** Long enough that the sheet has painted and the shelf is readable first. */
const WAKE_DELAY_MS = 400;

/**
 * Frames a second, and the reason there is a cap at all.
 *
 * The orbit turns at 6.5°/s. At that rate 30 fps is indistinguishable from 60 and costs half
 * as much, and what it is buying is not the menu's own smoothness — it is the headroom for
 * everything else the page is doing while this plays: fetching tours, generating four region
 * sketches, and on a machine with no GPU, rasterising all of it in software.
 */
const BACKDROP_FPS = 30;

export class MenuBackdrop {
  readonly root: HTMLElement;
  private readonly canvas: HTMLCanvasElement;

  private kit: SceneKit | null = null;
  private meshes: WorldMeshes | null = null;
  private route: RouteMeshes | null = null;
  private readonly orbit = new OrbitCamera();

  private world: World | null = null;
  private frameHandle = 0;
  private lastFrame = 0;
  private awake = false;
  private loading = false;
  /** Set once anything has gone wrong badly enough that retrying is just noise. */
  private gaveUp = false;
  private wakeTimer = 0;

  constructor() {
    this.canvas = el('canvas', { class: 'backdrop-canvas' });
    this.root = el('div', { class: 'backdrop' }, this.canvas);
  }

  /**
   * Show the city, building whatever is missing.
   *
   * Safe to call on every screen change: it is a no-op once running, and the delay means
   * bouncing through the lobby and back does not restart the work.
   */
  async wake(): Promise<void> {
    if (this.awake || this.gaveUp || !backdropWanted()) return;
    this.awake = true;
    if (this.wakeTimer) window.clearTimeout(this.wakeTimer);
    this.wakeTimer = window.setTimeout(() => {
      this.wakeTimer = 0;
      void this.build();
    }, WAKE_DELAY_MS);
  }

  /** Give the GPU back. The world payload is kept so waking again is meshes only. */
  sleep(): void {
    this.awake = false;
    if (this.wakeTimer) {
      window.clearTimeout(this.wakeTimer);
      this.wakeTimer = 0;
    }
    this.stopLoop();
    this.root.classList.remove('is-live');
    this.orbit.stop();
    this.clearMeshes();
    if (this.kit) {
      this.kit.dispose();
      this.kit = null;
    }
  }

  private async build(): Promise<void> {
    if (!this.awake || this.loading) return;
    this.loading = true;
    try {
      if (!this.world) this.world = await this.fetchWorld();
      // Waking and sleeping again while the fetch was in flight.
      if (!this.world || !this.awake) return;

      if (!this.kit) {
        this.kit = createSceneKit(this.canvas);
        // Two economies the race would never accept and a backdrop never notices. The
        // shadow pass is a second render of the whole city every frame, for shadows read at
        // three hundred metres through a vignette; and a background behind an opaque sheet
        // does not need a retina buffer, which on a 2x display is four times the fill.
        this.kit.setShadowsEnabled(false);
        this.kit.renderer.setPixelRatio(1);
        // A city turning slowly behind the menus is the one place a model village is exactly
        // the right look (DECISIONS.md §85).
        this.kit.setTiltShift(1);
      }
      this.resize();
      this.clearMeshes();

      this.meshes = buildWorldMeshes(this.world);
      this.kit.scene.add(this.meshes.group);
      this.route = buildRouteMeshes(this.world);
      this.kit.scene.add(this.route.group);
      this.meshes.setNight(this.kit.look.night);
      this.meshes.setWet(this.kit.look.wet);

      this.orbit.setWorld(this.world, new Skyline(this.world));
      this.orbit.reset();

      window.addEventListener('resize', this.onResize);
      this.startLoop();
      // Only now, so the fade is of a scene that is actually drawn rather than of a
      // clear-coloured canvas that becomes a city a frame later.
      this.root.classList.add('is-live');
    } catch (err) {
      // Includes the case that matters most: a browser that cannot give us a second WebGL
      // context. There is nothing to say to the player about it, and nothing to retry.
      this.gaveUp = true;
      console.info('[backdrop] not showing a city:', (err as Error).message);
    } finally {
      this.loading = false;
    }
  }

  private async fetchWorld(): Promise<World | null> {
    const res = await fetch('/api/showcase');
    const body = (await res.json()) as { ok: boolean; url?: string };
    if (!body.ok || !body.url) return null;
    const world = await fetch(body.url);
    if (!world.ok) throw new Error(`HTTP ${world.status}`);
    return (await world.json()) as World;
  }

  private clearMeshes(): void {
    if (this.meshes) {
      this.kit?.scene.remove(this.meshes.group);
      this.meshes.dispose();
      this.meshes = null;
    }
    if (this.route) {
      this.kit?.scene.remove(this.route.group);
      this.route.dispose();
      this.route = null;
    }
  }

  private startLoop(): void {
    if (this.frameHandle) return;
    this.lastFrame = performance.now();
    const interval = 1000 / BACKDROP_FPS;
    const frame = (): void => {
      this.frameHandle = requestAnimationFrame(frame);
      const now = performance.now();
      if (now - this.lastFrame < interval) return;
      // Clamped, because a tab left in the background for an hour comes back with a delta
      // that would spin the orbit through several revolutions in one step.
      const dt = Math.min(0.1, (now - this.lastFrame) / 1000);
      this.lastFrame = now;
      const kit = this.kit;
      if (!kit) return;
      this.orbit.update(kit.camera, dt);
      kit.update(dt);
      kit.render();
    };
    this.frameHandle = requestAnimationFrame(frame);
  }

  private stopLoop(): void {
    if (this.frameHandle) cancelAnimationFrame(this.frameHandle);
    this.frameHandle = 0;
    window.removeEventListener('resize', this.onResize);
  }

  private onResize = (): void => this.resize();

  private resize(): void {
    const width = this.root.clientWidth || window.innerWidth;
    const height = this.root.clientHeight || window.innerHeight;
    this.kit?.resize(width, height);
  }
}

/**
 * Whether to show a city at all.
 *
 * `?backdrop=off` mirrors the `?shadows=off` the renderer already honours, and exists for
 * the same reason: something visual that costs frames should have a switch that does not
 * require a code change to find out whether it was the problem. `?backdrop=on` forces it
 * back on, which is how the screenshots in `docs/` get taken on a machine that this function
 * would otherwise, correctly, refuse.
 *
 * Reduced motion is not a preference about animation quality — it is somebody telling the
 * browser that movement makes them ill — and a city sliding under the menu is exactly the
 * kind of slow continuous drift that request is about. There is no still version worth
 * showing, so the answer is the gradient.
 */
function backdropWanted(): boolean {
  if (typeof window === 'undefined') return false;
  const flag = new URLSearchParams(location.search).get('backdrop');
  if (flag === 'off') return false;
  if (flag === 'on') return true;
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return false;
  return hasHardwareWebGL();
}

/**
 * Is there a GPU, or is this being drawn by the CPU?
 *
 * A machine with no GPU does not get a decorative second renderer, and this is not a
 * marginal call: measured on the test suite's own SwiftShader browser, the menu with a city
 * behind it took **8.4 seconds** to do what took 0.6 without one, and picking a region in
 * the lobby went from 0.17 seconds to 10.6. Shadows off, a 30 fps cap and a 1x buffer all
 * helped and none of them came close to fixing it — software rasterising a whole city is
 * simply the wrong thing to spend a menu on, and the page starves while it happens.
 *
 * The people this protects are not hypothetical: a VM, a remote desktop, a browser that has
 * blocklisted a driver, and every headless CI browser in the world land here.
 *
 * A browser that masks the renderer string gets the benefit of the doubt, because the
 * masking is a privacy measure and says nothing about the hardware.
 */
function hasHardwareWebGL(): boolean {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2') ?? canvas.getContext('webgl');
    if (!gl) return false;
    const info = gl.getExtension('WEBGL_debug_renderer_info');
    const renderer = info ? String(gl.getParameter(info.UNMASKED_RENDERER_WEBGL) ?? '') : '';
    // Free the probe context immediately: browsers cap how many a page may hold, and this
    // one has done its job.
    gl.getExtension('WEBGL_lose_context')?.loseContext();
    if (!renderer) return true;
    return !/swiftshader|llvmpipe|softpipe|software|basic render|microsoft basic/i.test(renderer);
  } catch {
    return false;
  }
}
